import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QuoteIcon } from '@/components/icons/QuoteIcon';
import { apiGetRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';
import { usePreferredTimeZone } from '@/hooks/usePreferredTimeZone';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { LocalStorageKeys } from '@/constants/localStorage';

interface TimeBit {
    id: number;
    content: string;
    author: string | null;
}

/**
 * 格言セクションコンポーネント
 */
const QuoteSection: React.FC = () => {
    const { t, i18n } = useTranslation('notifications');
    const { userInfo } = useUser();
    const preferredTimeZone = usePreferredTimeZone();
    const [quote, setQuote] = useState<TimeBit | null>(null);
    const [loading, setLoading] = useState(true);
    
    // 言語に応じたフォントファミリーを決定（標準フォントのセリフ体）
    const quoteFontFamily = i18n.language?.startsWith('ja') 
        ? "'Hiragino Mincho Pro', 'Yu Mincho', 'YuMincho', 'MS 明朝', 'MS Mincho', serif" 
        : "'Georgia', 'Palatino', 'Palatino Linotype', 'Book Antiqua', 'Times New Roman', 'Times', serif";

    // 今日の格言を取得（1日1回のみ、ローカルストレージでキャッシュ管理）
    useEffect(() => {
        const fetchDailyQuote = async () => {
            if (!userInfo?.userId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // 現在の言語を取得
                const currentLanguage = i18n.language?.startsWith('ja') ? 'ja' : 'en';
                
                // 今日の日付を取得（ユーザーのタイムゾーン設定に合わせたタイムゾーン、YYYY-MM-DD形式）
                const now = new Date();
                const formatter = new Intl.DateTimeFormat('en-CA', {
                    ...(preferredTimeZone && { timeZone: preferredTimeZone }),
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                });
                const today = formatter.format(now);

                // ローカルストレージからキャッシュを取得
                const cached = await getLocalStorage(userInfo.userId, LocalStorageKeys.QUOTE_CACHE);
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        // キャッシュがある場合、かつ今日の日付、かつ言語が一致する場合のみキャッシュを使用
                        if (parsed.date === today && parsed.language === currentLanguage && parsed.quote) {
                            setQuote({
                                id: 0,
                                content: parsed.quote.content,
                                author: parsed.quote.author,
                            });
                            setLoading(false);
                            return;
                        }
                    } catch (e) {
                        // パースエラー時は無視してAPIから取得
                        console.warn('Failed to parse cached quote:', e);
                    }
                }

                // APIから取得（キャッシュがない、または日付が変わった、または言語が変わった場合）
                const response = await apiGetRequest('/api/time-bits/daily');

                if (!response.ok) {
                    throw new Error('Failed to fetch daily quote');
                }

                const data: TimeBit | null = await response.json();
                if (data) {
                    setQuote(data);
                    // ローカルストレージにキャッシュを保存（言語情報も含める）
                    await setLocalStorage(
                        userInfo.userId,
                        LocalStorageKeys.QUOTE_CACHE,
                        JSON.stringify({
                            date: today,
                            language: currentLanguage,
                            quote: {
                                content: data.content,
                                author: data.author,
                            },
                        }),
                    );
                } else {
                    setQuote(null);
                }
            } catch (err) {
                // エラーが発生した場合も、エラー表示せずに何も表示しない
                console.error('Error fetching daily quote:', err);
                setQuote(null);
            } finally {
                setLoading(false);
            }
        };

        fetchDailyQuote();
    }, [userInfo?.userId, i18n.language, preferredTimeZone]);

    return (
        <div className="flex flex-col h-full w-full min-w-0 bg-white rounded-lg shadow-sm p-4">
            {/* ヘッダー */}
            <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                <QuoteIcon className="size-4 text-gray-700" />
                <h3 className="text-sm font-bold text-gray-700">{t('quote.title')}</h3>
            </div>

            {/* コンテンツ */}
            <div className="flex-1 overflow-y-auto min-h-0 flex flex-col -mx-4 -my-4 px-4 py-4">
                {loading ? (
                    <p className="text-sm text-gray-500">読み込み中...</p>
                ) : quote ? (
                    <div className="w-full h-full p-4 space-y-3 flex flex-col justify-center">
                        <p 
                            className="text-sm text-gray-600 leading-relaxed font-bold border-b border-gray-200 pb-2"
                            style={{ fontFamily: quoteFontFamily }}
                        >
                            {quote.content}
                        </p>
                        {quote.author && (
                            <p 
                                className="text-xs text-gray-400 text-right font-bold"
                                style={{ fontFamily: quoteFontFamily }}
                            >
                                — {quote.author}
                            </p>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default QuoteSection;
